import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getPostsRoot } from 'redux/selectors';
import {
	useCreateDispatches,
	usePasswordConfirm,
	useCSWDelete,
} from 'helpers/hooks';
import * as service from 'services';
import { getRouteRoot, isCaseManagerUser } from 'helpers';
import useGetPostFunction from './useGetPostFunction';

export default function useGetCallbacks(post) {
	const { id, postDraftId, childId: postChildId } = post;
	const user = useSelector((state) => state.user.user);
	const isCaseManager = isCaseManagerUser(user);
	const checkDateDayOld = (date) => {
		const oneDay = new Date().getTime() - 1 * 24 * 60 * 60 * 1000;
		return new Date(date).getTime() > oneDay;
	};
	const isOwnPost = post.createdBy?.id === user.id;
	const isDayOld = checkDateDayOld(post.createdAt);
	const { childId: paramsChildId } = useParams();
	const childId = paramsChildId || postChildId;
	const root = useSelector(getPostsRoot);
	const { pathname } = useLocation();
	const routeRoot = getRouteRoot(root, pathname);
	const childPending = useSelector(
		(state) => state.lifebooks.child.pendingPostCount
	);
	const { push } = useHistory();
	const onPasswordConfirm = usePasswordConfirm();
	const onCSWDeleteConfirm = useCSWDelete();
	const fetchPostsFunction = useGetPostFunction();

	const {
		setLoader,
		setSnackbar,
		removePost,
		setChildPendCount,
		setPostList,
		setPost,
	} = useCreateDispatches();

	const onEditPost = () => {
		const draftPost = post.postDraftId ? true : false;

		if (user.type === 'agencyuser' && !post.canDelete && !draftPost) {
			setSnackbar(
				{
					variant: 'error',
					message: 'Cannot edit post because it was created by another agency',
				},
				{ log: true }
			);
		} else {
			push({
				pathname: `/${routeRoot}/post/${childId}/${id || postDraftId}`,
				state: post,
			});
		}
	};

	const onApprovePost = async () => {
		if (id) {
			setLoader(true);
			try {
				const approveResponse = await service.approvePost(post);
				if (!approveResponse.error) {
					removePost({ id });
					await service
						.getCurrentChild(childId, { fetch: true })
						.catch(() => null);
					if (!isCaseManager && Number.isFinite(Number(childPending))) {
						setChildPendCount({
							childId,
							count: Number(childPending) - 1,
						});
					}
					setSnackbar({
						variant: 'success',
						message: 'Success',
					});
				}
			} catch (err) {
				setSnackbar(
					{
						variant: 'error',
						message:
							err.response?.data?.message || 'Post could not be approved.',
					},
					{ log: true }
				);
			} finally {
				setLoader(false);
			}
		}
	};

	const handleDeleteDraft = async () => {
		return service
			.deleteDraft(postDraftId)
			.then(() => {
				setSnackbar({
					variant: 'success',
					message: 'Draft deleted successfully',
				});
				removePost({ id, postDraftId });
				service.getCurrentChild(childId, { fetch: true });
			})
			.catch((err) => {
				setSnackbar(
					{ variant: 'error', message: err.response?.data.message },
					{ log: true }
				);
				if (isCaseManager) throw err;
			})
			.finally(() => {
				setLoader(false);
			});
	};

	const handleDelete = async (pass) => {
		let message;
		if (user.type === 'child') {
			if (isOwnPost && isDayOld) {
				message = 'Post deleted succesfully';
			} else {
				message = 'Post removed';
			}
		} else {
			message = 'Post deleted successfully';
		}

		return service
			.archivePost(id, pass)
			.then(() => {
				setSnackbar({
					variant: 'success',
					message: `${message}`,
				});
				removePost({ id });
				service.getCurrentChild(childId, { fetch: true });
				if (
					!isCaseManager &&
					post.status !== 'approved' &&
					Number.isFinite(Number(childPending))
				) {
					setChildPendCount({
						childId,
						count: Number(childPending) - 1,
					});
				}
			})
			.catch((err) => {
				const message = err.response?.data?.message
					? err.response.data.message
					: 'Incorrect Password';

				setSnackbar({ variant: 'error', message: message }, { log: true });
				throw err;
			})
			.finally(() => {
				setLoader(false);
			});
	};

	const deletePost = async (e) => {
		if (user.type === 'agencyuser' && !post.canDelete) {
			setSnackbar(
				{
					variant: 'error',
					message:
						'Cannot delete post because it was created by another agency',
				},
				{ log: true }
			);
		} else {
			return handleDelete(e);
		}
	};

	const onDeletePost = () => {
		if (postDraftId) {
			if (isCaseManager) {
				onPasswordConfirm(handleDeleteDraft, {
					message: 'Are you sure you want to delete this draft?',
					requirePassword: false,
					submitLabel: 'Delete',
				});
			} else {
				handleDeleteDraft();
			}
		} else {
			if (isCaseManager) {
				onPasswordConfirm(deletePost, {
					message: 'Are you sure you want to delete this post?',
					requirePassword: false,
					submitLabel: 'Delete',
				});
			} else if (user.type === 'csw') {
				setPost(post);
				onCSWDeleteConfirm({
					message:
						'Are you sure you want to delete this post? Please confirm your password to proceed.',
				});
			} else {
				onPasswordConfirm((e) => deletePost(e), {
					message:
						'Are you sure you want to delete this post? Please confirm your password to proceed.',
				});
			}
		}
	};

	const onMoveToVault = () => {
		if (id) {
			setLoader(true);
			removePost(id);
			service
				.moveToVault(id)
				.then(({ data }) => {
					data && removePost({ id });
					service.getCurrentChild(childId, { fetch: true });
					if (post.status !== 'approved') {
						setChildPendCount({ childId, count: childPending - 1 });
					}
				})
				.finally(() => {
					setLoader(false);
				});
		}
	};

	const onMoveFromVault = () => {
		onPasswordConfirm(moveFromVault, {
			message: 'Are you sure you want to move this post to the Timeline?',
			requirePassword: !isCaseManager,
			submitLabel: 'Move',
		});
	};

	const moveFromVault = () => {
		if (id) {
			setLoader(true);
			removePost(id);
			return service
				.moveFromVault(id)
				.then(({ data }) => {
					data && removePost({ id });
					service.getCurrentChild(childId, { fetch: true });
					setSnackbar({
						variant: 'success',
						message: 'Successfully restored post',
					});
				})
				.catch((err) => {
					setSnackbar(
						{
							variant: 'error',
							message:
								err.response?.data?.message ||
								'Post could not be restored.',
						},
						{ log: true }
					);
					throw err;
				})
				.finally(() => {
					setLoader(false);
				});
		}
	};

	const onMoveServicePost = async () => {
		setLoader(true);
		const moved = await service.moveServicePost(id, !post.displayNSPInTimeline);

		if (moved) {
			const data = await fetchPostsFunction(childId, {});
			if (data) {
				setPostList(data.data?.posts);
			}
		}
		setLoader(false);
	};

	return {
		onEditPost,
		onApprovePost,
		onDeletePost,
		onMoveToVault,
		onMoveServicePost,
		onMoveFromVault,
	};
}
